import type { StringKey } from '../i18n/es';

/**
 * The closing sequence after the last jump of the campaign (GDD §3.3, §13.2, issue #136), as a pure step machine with no DOM
 * and no `three`: REVELACION (Mr. Smith is Agente Morfi of the DEA), ESCAPE (Turbina jumps again with the bag), HARINA (the bag
 * shows the seal and stays sealed) and CREDITOS. Each beat of the story lasts a few seconds on its own; skipping goes straight to
 * the credits from any step, and skipping in the credits ends the sequence (the screen then returns to the title). Nothing here
 * decides what is drawn: the screen reads the step and the lines.
 */
export type ClosingStep = 'REVELACION' | 'ESCAPE' | 'HARINA' | 'CREDITOS';

export const CLOSING_STEPS: readonly ClosingStep[] = ['REVELACION', 'ESCAPE', 'HARINA', 'CREDITOS'];

/** How long each beat of the story lasts before the next one starts by itself (ms). */
export const BEAT_MS = 5000;
/** How long the credits scroll before the sequence ends by itself (ms). */
export const CREDITS_MS = 30000;

/** A line of a character, with the key of the name of who says it and the key of the text (8 words at most, GDD §13.4). */
export interface ClosingLine {
  speaker: StringKey;
  text: StringKey;
}

/** The lines of each beat of the story; the credits have none. Names are all parody names (GDD §3.4). */
export const CLOSING_LINES: Readonly<Record<ClosingStep, readonly ClosingLine[]>> = {
  REVELACION: [
    { speaker: 'ui.cierre.speaker.narrator', text: 'voice.closing.reveal.glasses' },
    { speaker: 'ui.cierre.speaker.smith', text: 'voice.closing.reveal.morfi' },
  ],
  ESCAPE: [
    { speaker: 'ui.cierre.speaker.radio', text: 'voice.closing.escape.radio' },
    { speaker: 'ui.cierre.speaker.turbina', text: 'voice.closing.escape.turbina' },
  ],
  HARINA: [
    { speaker: 'ui.cierre.speaker.morfi', text: 'voice.closing.flour.morfi' },
    { speaker: 'ui.cierre.speaker.chiva', text: 'voice.closing.flour.chiva' },
  ],
  CREDITOS: [],
};

export type ClosingListener = (step: ClosingStep) => void;

export class ClosingSequence {
  #step: ClosingStep;
  #elapsed = 0;
  #done = false;
  readonly #history: ClosingStep[];
  readonly #listeners = new Set<ClosingListener>();

  /** Starts at the first beat after the last level, or at the credits when they are opened from MISIONES. */
  constructor(start: ClosingStep = 'REVELACION') {
    this.#step = start;
    this.#history = [start];
  }

  get step(): ClosingStep {
    return this.#step;
  }

  /** Whether the sequence is over: the screen returns to the title. */
  get done(): boolean {
    return this.#done;
  }

  /** The steps shown so far, in order. */
  get history(): readonly ClosingStep[] {
    return this.#history;
  }

  /** Milliseconds in the current step. */
  get elapsed(): number {
    return this.#elapsed;
  }

  get lines(): readonly ClosingLine[] {
    return CLOSING_LINES[this.#step];
  }

  /** Time passes: each beat gives way to the next one, and the credits end by themselves. */
  tick(ms: number): void {
    if (this.#done) return;
    this.#elapsed += ms;
    if (this.#step === 'CREDITOS') {
      if (this.#elapsed >= CREDITS_MS) this.#finish();
      return;
    }
    while (this.#elapsed >= BEAT_MS && !this.#inCredits()) {
      const carried = this.#elapsed - BEAT_MS;
      this.#goTo(CLOSING_STEPS[CLOSING_STEPS.indexOf(this.#step) + 1]!);
      this.#elapsed = carried;
    }
  }

  /** "Saltar" or Escape: from any beat to the credits, and from the credits to the end. */
  skip(): void {
    if (this.#done) return;
    if (this.#step === 'CREDITOS') this.#finish();
    else this.#goTo('CREDITOS');
  }

  subscribe(listener: ClosingListener): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  #inCredits(): boolean {
    return this.#step === 'CREDITOS';
  }

  #goTo(step: ClosingStep): void {
    this.#step = step;
    this.#elapsed = 0;
    this.#history.push(step);
    for (const listener of this.#listeners) listener(step);
  }

  #finish(): void {
    this.#done = true;
    for (const listener of this.#listeners) listener(this.#step);
  }
}
